import type { KycSection } from './formLayout'

export type KycPosition = 1 | 2 | 3

const POSITION_COUNT = 3

// Figma groups country/address in L1 and occupation/purpose in L2.
const POSITION_BY_SECTION: Record<KycSection, KycPosition> = {
  NAME: 1,
  DOB: 1,
  NATIONALITY: 2,
  COUNTRY: 2,
  ADDRESS: 2,
  SSN: 3,
  PHONE: 2,
  OCCUPATION: 2,
  EMPLOYMENT: 2,
  PURPOSE: 2,
  SOURCE_OF_WEALTH: 2,
  INCOME: 2,
  ACTIVITY: 2,
  DETAILS: 2,
}

/** Where a page stands in the pages walked so far, this round's included. */
export interface KycWalkOrder {
  index: number
  count: number
  /** The dot the rounds before this one reached; the walk never drops below it. */
  floor?: KycPosition
}

/**
 * The pages walked before this round, and this round's own, so a later
 * round's dot never drops back behind an earlier round's.
 */
export interface KycWalk {
  formId: string
  before: number
  pages: number
  floor: KycPosition
}

export function walkRound(
  walk: KycWalk | undefined,
  formId: string,
  pages: number,
): KycWalk {
  if (walk === undefined) {
    return { formId, before: 0, pages, floor: 1 }
  }
  if (walk.formId === formId) {
    return walk
  }
  return {
    formId,
    before: walk.before + walk.pages,
    pages,
    floor: kycFormPosition('DETAILS', walkOrder(walk, walk.pages - 1)),
  }
}

export function walkOrder(walk: KycWalk, step: number): KycWalkOrder {
  return {
    index: walk.before + step,
    count: walk.before + walk.pages,
    floor: walk.floor,
  }
}

/**
 * The lit dot for a page: its place in the pages actually walked, bucketed
 * into the three positions, with the last page known lighting the last dot.
 * Without an order to read, the section's Figma grouping stands in.
 */
export function kycFormPosition(
  section: KycSection,
  order?: KycWalkOrder,
): KycPosition {
  if (order === undefined || order.count <= 0) {
    return POSITION_BY_SECTION[section]
  }
  const at = Math.min(Math.max(order.index, 0), order.count - 1)
  const bucket =
    order.count > 1 && at === order.count - 1
      ? POSITION_COUNT
      : Math.floor((at * POSITION_COUNT) / order.count) + 1
  return Math.max(bucket, order.floor ?? 1) as KycPosition
}
